/* eslint-disable no-console */
import getCsrfHeader from '@sd-utility/csrf';
import { ATGSFLGetItemsResponse } from './types/ATGSFLResponse';
import { SFLProduct } from './types/search-response';
import SavedItemList from '../../tests/__mocks__/save-for-later-list';

const csrfTokenStorageKey = 'sfl_csrf_token';

const isStorageReady = () =>
  typeof window !== 'undefined' &&
  sessionStorage &&
  typeof sessionStorage.getItem === 'function' &&
  typeof sessionStorage.setItem === 'function';

function getCsrfTokenFromStorage() {
  let csrfToken = null;
  if (isStorageReady()) {
    csrfToken = sessionStorage.getItem(csrfTokenStorageKey);
    if (!csrfToken) {
      console.log('getCsrfTokenFromStorage: csrfToken is apsend in Session Storage at the moment');
    }
  }
  return csrfToken;
}

function setCsrfTokenToStorage(csrfToken: string) {
  if (isStorageReady()) {
    sessionStorage.setItem(csrfTokenStorageKey, csrfToken);
  }
}

const getProductsURL = () =>
  `/rest/model/sdg/savedforlater/SavedForLaterActor/list?_dynSessConf=${getCsrfTokenFromStorage()}`;

const addRemoveURL = (action: string, productId = '') =>
  `/rest/model/sdg/savedforlater/SavedForLaterActor/${action}?_dynSessConf=${getCsrfTokenFromStorage()}`.concat(
    productId !== '' ? `&productId=${productId}` : '',
  );

const makeRequestToSaveForLaterATG = async (url: string, isLocal: boolean, methodType = 'GET') => {
  // eslint-disable-next-line no-underscore-dangle
  if (isLocal) {
    const savedItems = SavedItemList();
    return savedItems;
  }

  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...getCsrfHeader(),
    },
    method: methodType,
    body: null,
  });
  if (response.ok) {
    return response.json();
  }
  let errorMessage;
  const statusCode = response.status;

  try {
    const errorResponse = await response.json();
    if (errorResponse.message) {
      errorMessage = errorResponse.message;
    }
  } catch (error) {
    errorMessage = 'There was an error calling save for later service';
  }
  throw new Error(`${errorMessage} (Status Code: ${statusCode})`);
};

const getSFLItems = async (isLocal: boolean): Promise<ATGSFLGetItemsResponse> => {
  const url = getProductsURL();
  let saveForLaterList: ATGSFLGetItemsResponse = {
    savedProducts: [],
  };
  try {
    saveForLaterList = await makeRequestToSaveForLaterATG(url, isLocal);
  } catch (error) {
    console.error(error);
    throw error;
  }
  return saveForLaterList;
};

const addProductToSaveForLater = async (isLocal: boolean, productId: string) => {
  const url = addRemoveURL('addProduct', productId);
  let saveForLaterData = {};
  try {
    saveForLaterData = await makeRequestToSaveForLaterATG(url, isLocal, 'POST');
  } catch (error) {
    throw new Error(error);
  }
  return saveForLaterData;
};

const removeProductFromSaveForLater = async (isLocal: boolean, productId: string) => {
  const url = addRemoveURL('removeProduct', productId);
  let saveForLaterData = {};

  try {
    saveForLaterData = await makeRequestToSaveForLaterATG(url, isLocal, 'POST');
  } catch (error) {
    console.error(error);
    throw new Error(error);
  }
  return saveForLaterData;
};

function getSavedProductsFromStorage() {
  const sflItems = sessionStorage.getItem('sfl_product_list');
  return sflItems ? JSON.parse(sflItems).savedProducts : [];
}

function setSavedProductsToStorage(savedProducts: SFLProduct[]) {
  sessionStorage.setItem('sfl_product_list', JSON.stringify({ savedProducts }));
}

function addProductToSessionStorage(productId: string) {
  try {
    const savedProducts = getSavedProductsFromStorage();
    savedProducts.push({ productId });
    setSavedProductsToStorage(savedProducts);
  } catch (error) {
    /* istanbul ignore next */
    console.log('Error adding product to SFL session storage');
    throw new Error(error);
  }
}

function removeProductFromSessionStorage(productId: string) {
  try {
    const savedProducts = getSavedProductsFromStorage();
    const productIndex = savedProducts.findIndex((item: SFLProduct) => item.productId === productId);
    if (productIndex !== -1) {
      savedProducts.splice(productIndex, 1);
      setSavedProductsToStorage(savedProducts);
    }
  } catch (error) {
    /* istanbul ignore next */
    console.log('Error removing product from SFL session storage');
    throw new Error(error);
  }
}

export {
  makeRequestToSaveForLaterATG,
  addRemoveURL,
  getSavedProductsFromStorage,
  setSavedProductsToStorage,
  addProductToSessionStorage,
  removeProductFromSessionStorage,
  getSFLItems,
  addProductToSaveForLater,
  removeProductFromSaveForLater,
  setCsrfTokenToStorage,
};
